// Réglages de jeu des réglages cachés (déplacement, gyroscope, kamis, partie, batterie, aide, son) : les curseurs
// vivent dans la fenêtre des réglages (ui/settingsDialog.js). Chaque changement est signalé tout de suite à `onChange`
// (appliqué et enregistré à l'instant, sans bouton « Enregistrer »). Chaque curseur affiche sa valeur courante, avec
// son unité, à côté de son titre (mise à jour pendant qu'on le déplace).
import { $ } from './dom.js';

/** @param {{ onChange?: (settings: object, batteryLevel: number) => void }} [options] */
export function createDevSettingsView({ onChange } = {}) {
  const inputs = {
    worldGrowth: $('devWorldGrowth'),
    captureRadius: $('devCaptureRadius'),
    panSpeed: $('devPanSpeed'),
    panMaxSpeed: $('devPanMaxSpeed'),
    panAccelTime: $('devPanAccelTime'),
    wheelSensitivity: $('devWheelSensitivity'),
    dragFriction: $('devDragFriction'),
    gyroHorizonOffset: $('devGyroHorizonOffset'),
    gyroYawSpeed: $('devGyroYawSpeed'),
    gyroPitchSpeed: $('devGyroPitchSpeed'),
    gyroSmoothing: $('devGyroSmoothing'),
    photoCostPercent: $('devPhotoCost'),
    familySize: $('devFamilySize'),
    ghostSpeed: $('devGhostSpeed'),
    summonDelay: $('devSummonDelay'),
    summonSpeed: $('devSummonSpeed'),
    hintDelay: $('devHintDelay'),
    bpm: $('devBpm'),
    startLevel: $('devStartLevel'),
    batteryLevel: $('devBatteryLevel'),
    batterySeconds: $('devBatterySeconds'),
    chargeMinutes: $('devChargeMinutes'),
  };
  const lblStartLevel = $('lblStartLevel');

  // Valeur affichée à côté du titre, d'après la valeur brute du curseur (certains sont en dixièmes)
  const tenth = (v) => (v / 10).toFixed(1);
  const formatters = {
    worldGrowth: (v) => `+${v}`,
    captureRadius: (v) => `${v}`,
    panSpeed: (v) => `${v}`,
    panMaxSpeed: (v) => `${v}`,
    panAccelTime: (v) => `${tenth(v)} s`,
    wheelSensitivity: (v) => `x${tenth(v)}`,
    dragFriction: (v) => `${v}`,
    gyroHorizonOffset: (v) => `${v > 0 ? '+' : ''}${v}°`,
    gyroYawSpeed: (v) => `x${tenth(v)}`,
    gyroPitchSpeed: (v) => `x${tenth(v)}`,
    gyroSmoothing: (v) => `${v} %`,
    photoCostPercent: (v) => `${v} % (${Math.round(100 / v)} photos)`,
    familySize: (v) => (v === 0 ? 'Auto' : `${v} / 15`),
    ghostSpeed: (v) => `${v}`,
    summonDelay: (v) => (v === 0 ? 'tout de suite' : `${v} s`),
    summonSpeed: (v) => `${v}`,
    hintDelay: (v) => `${v} s`,
    bpm: (v) => `${v}`,
    startLevel: (v) => `${v}`,
    batteryLevel: (v) => `${v} %`,
    batterySeconds: (v) => `${v} s`,
    chargeMinutes: (v) => `${v} min`,
  };
  function refreshValue(key) {
    const el = document.querySelector(`[data-value-for="${inputs[key].id}"]`);
    if (el) el.textContent = formatters[key](parseInt(inputs[key].value, 10));
  }
  const refreshValues = () => Object.keys(inputs).forEach(refreshValue);
  for (const key of Object.keys(inputs)) {
    inputs[key].addEventListener('input', () => {
      refreshValue(key);
      const { batteryLevel, ...settings } = read();
      onChange?.(settings, batteryLevel, key);
    });
  }

  inputs.startLevel.addEventListener('input', (e) => {
    lblStartLevel.innerText = e.target.value;
  });

  /**
   * Remplit les curseurs avec les réglages courants.
   * @param {number} [batteryPercent]  charge actuelle de la batterie (0..100), proposée par le curseur « charge actuelle »
   */
  function populate(settings, batteryPercent = 100) {
    inputs.worldGrowth.value = settings.worldGrowth;
    inputs.captureRadius.value = settings.captureRadius;
    inputs.panSpeed.value = settings.panSpeed;
    inputs.panMaxSpeed.value = settings.panMaxSpeed;
    inputs.panAccelTime.value = Math.round(settings.panAccelTime * 10);
    inputs.wheelSensitivity.value = Math.round(settings.wheelSensitivity * 10);
    inputs.dragFriction.value = settings.dragFriction;
    inputs.gyroHorizonOffset.value = settings.gyroHorizonOffset;
    inputs.gyroYawSpeed.value = Math.round(settings.gyroYawSpeed * 10);
    inputs.gyroPitchSpeed.value = Math.round(settings.gyroPitchSpeed * 10);
    inputs.gyroSmoothing.value = settings.gyroSmoothing;
    inputs.photoCostPercent.value = settings.photoCostPercent;
    inputs.familySize.value = settings.familySize;
    inputs.ghostSpeed.value = settings.ghostSpeed;
    inputs.summonDelay.value = settings.summonDelay;
    inputs.summonSpeed.value = settings.summonSpeed;
    inputs.hintDelay.value = settings.hintDelay;
    inputs.bpm.value = settings.musicBpm;
    inputs.startLevel.value = settings.startLevel;
    inputs.batteryLevel.value = Math.max(0, Math.min(100, Math.round(batteryPercent)));
    inputs.batterySeconds.value = settings.batterySeconds;
    inputs.chargeMinutes.value = settings.chargeMinutes;
    lblStartLevel.innerText = settings.startLevel;
    refreshValues();
  }

  /** Lit le formulaire et retourne un nouvel objet de réglages */
  function read() {
    return {
      worldGrowth: parseInt(inputs.worldGrowth.value, 10),
      captureRadius: parseInt(inputs.captureRadius.value, 10),
      panSpeed: parseInt(inputs.panSpeed.value, 10),
      panMaxSpeed: parseInt(inputs.panMaxSpeed.value, 10),
      panAccelTime: parseInt(inputs.panAccelTime.value, 10) / 10,
      wheelSensitivity: parseInt(inputs.wheelSensitivity.value, 10) / 10,
      dragFriction: parseInt(inputs.dragFriction.value, 10),
      gyroHorizonOffset: parseInt(inputs.gyroHorizonOffset.value, 10),
      gyroYawSpeed: parseInt(inputs.gyroYawSpeed.value, 10) / 10,
      gyroPitchSpeed: parseInt(inputs.gyroPitchSpeed.value, 10) / 10,
      gyroSmoothing: parseInt(inputs.gyroSmoothing.value, 10),
      photoCostPercent: parseInt(inputs.photoCostPercent.value, 10),
      familySize: parseInt(inputs.familySize.value, 10),
      ghostSpeed: parseInt(inputs.ghostSpeed.value, 10),
      summonDelay: parseInt(inputs.summonDelay.value, 10),
      summonSpeed: parseInt(inputs.summonSpeed.value, 10),
      hintDelay: parseInt(inputs.hintDelay.value, 10),
      musicBpm: parseInt(inputs.bpm.value, 10),
      startLevel: parseInt(inputs.startLevel.value, 10),
      batteryLevel: parseInt(inputs.batteryLevel.value, 10),
      batterySeconds: parseInt(inputs.batterySeconds.value, 10),
      chargeMinutes: parseInt(inputs.chargeMinutes.value, 10),
    };
  }

  return { populate, read };
}
